import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Paper,
  Select,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import { Person as PersonIcon, Save as SaveIcon, UploadFile as UploadFileIcon } from '@mui/icons-material';
import { APIProvider } from '@vis.gl/react-google-maps';
import PageHeader from '../common/PageHeader';
import Loading from '../common/Loading';
import ErrorMessage from '../common/ErrorMessage';
import PlaceAutocomplete from '../common/PlaceAutocomplete';
import {
  clearCurrentMedicalTransportOrder,
  createMedicalTransportOrder,
  fetchMedicalTransportOrderById,
  updateMedicalTransportOrder
} from '../../redux/slices/medicalTransportOrdersSlice';
import { fetchCustomers } from '../../redux/slices/customersSlice';
import { SPECIAL_CAPABILITIES, VEHICLE_TYPE_OPTIONS } from '../../config/vehicleTypes';
import {
  APPROVAL_STATUS_OPTIONS,
  BILLING_STATUS_OPTIONS,
  CARE_LEVEL_OPTIONS,
  COPAYMENT_STATUS_OPTIONS,
  DOCUMENT_STATUS_OPTIONS,
  ELIGIBILITY_PROOF_TYPE_OPTIONS,
  MOBILITY_MARK_OPTIONS,
  PERSON_GROUP_STATUS_OPTIONS,
  ROUTE_SCOPE_OPTIONS,
  TREATMENT_TYPE_OPTIONS,
  toDateInputValue
} from './medicalTransportOrderOptions';
import { uploadAndScanPrescription } from '../../services/prescriptionScanService';
import { mapScanToFormState } from './scanMapping';

const FACILITY_KEYWORDS_BY_TREATMENT = {
  dialysis: 'Dialysezentrum',
  chemo: 'Onkologie Chemotherapie',
  radiation: 'Strahlentherapie',
  rehab: 'Reha Klinik',
  therapy: 'Therapiezentrum',
  doctor: 'Arztpraxis',
  hospital: 'Krankenhaus',
  other: 'medizinische Einrichtung'
};

const isGenericFacilityHint = (value = '') => (
  !value.trim() ||
  /^[☐☑☒✔✓Xx]\s*/.test(value.trim()) ||
  /hochfrequente behandlung|vergleichbarer ausnahmefall|dialyse,\s*onkol\.\s*chemo|chemo-\s*oder\s*strahlentherapie/i.test(value)
);

const addressCompletenessScore = (value = '') => {
  const text = value.trim();
  if (!text) return 0;
  let score = 0;
  if (/\b\d{5}\b/.test(text)) score += 2;
  if (/\b\d{1,4}\s*[a-z]?\b/i.test(text)) score += 2;
  if (/(straße|strasse|str\.|weg|allee|platz|ring|gasse|damm|ufer|chaussee)/i.test(text)) score += 2;
  if (text.includes(',')) score += 1;
  return score;
};

const mergeRoutePatchWithoutAddressRegression = (basePatch, routePatch) => {
  if (!routePatch) return {};

  const nextRoutePatch = { ...routePatch };
  const baseAddress = basePatch.pickupAddress || '';
  const routeAddress = routePatch.pickupAddress || '';

  if (
    baseAddress &&
    routeAddress &&
    addressCompletenessScore(baseAddress) >= addressCompletenessScore(routeAddress)
  ) {
    delete nextRoutePatch.pickupAddress;
  }

  return nextRoutePatch;
};

const waitForGoogleMapsPlaces = async () => {
  for (let attempt = 0; attempt < 30; attempt += 1) {
    if (window.google?.maps?.places && window.google?.maps?.Geocoder) {
      return window.google.maps;
    }
    if (window.google?.maps?.importLibrary) {
      try {
        await window.google.maps.importLibrary('places');
        return window.google.maps;
      } catch (error) {
        // retry below
      }
    }
    await new Promise(resolve => setTimeout(resolve, 150));
  }
  return null;
};

const geocodeAddress = async (maps, address) => new Promise(resolve => {
  if (!address?.trim()) {
    resolve(null);
    return;
  }

  const geocoder = new maps.Geocoder();
  geocoder.geocode({ address, componentRestrictions: { country: 'DE' } }, (results, status) => {
    if (status !== 'OK' || !results?.[0]?.geometry?.location) {
      resolve(null);
      return;
    }
    const result = results[0];
    resolve({
      address: result.formatted_address || address,
      location: {
        latitude: result.geometry.location.lat(),
        longitude: result.geometry.location.lng()
      },
      latLng: result.geometry.location
    });
  });
});

const getPlaceDetails = async (maps, service, place) => new Promise(resolve => {
  if (!place?.place_id) {
    resolve(place);
    return;
  }

  service.getDetails({
    placeId: place.place_id,
    fields: ['name', 'formatted_address', 'geometry']
  }, (details, status) => {
    resolve(status === maps.places.PlacesServiceStatus.OK && details ? details : place);
  });
});

const findNearestFacility = async ({ pickupAddress, treatmentType, facilityHint }) => {
  const maps = await waitForGoogleMapsPlaces();
  if (!maps || !pickupAddress?.trim()) return null;

  const pickup = await geocodeAddress(maps, pickupAddress);
  if (!pickup?.latLng) return null;

  const keyword = !isGenericFacilityHint(facilityHint)
    ? facilityHint.trim()
    : (FACILITY_KEYWORDS_BY_TREATMENT[treatmentType] || FACILITY_KEYWORDS_BY_TREATMENT.other);
  const fallbackKeyword = FACILITY_KEYWORDS_BY_TREATMENT[treatmentType] || FACILITY_KEYWORDS_BY_TREATMENT.other;
  const service = new maps.places.PlacesService(document.createElement('div'));

  const searchNearest = (searchKeyword) => new Promise(resolve => {
    service.nearbySearch({
      location: pickup.latLng,
      rankBy: maps.places.RankBy.DISTANCE,
      keyword: searchKeyword
    }, (results, status) => {
      resolve(status === maps.places.PlacesServiceStatus.OK && results?.length ? results[0] : null);
    });
  });
  let place = await searchNearest(keyword);
  if (!place && keyword !== fallbackKeyword) {
    place = await searchNearest(fallbackKeyword);
  }

  if (!place) {
    return {
      pickupAddress: pickup.address,
      pickupLatitude: pickup.location.latitude,
      pickupLongitude: pickup.location.longitude
    };
  }

  const details = await getPlaceDetails(maps, service, place);
  const address = details.formatted_address || details.vicinity || '';
  const name = details.name || place.name || '';
  const destinationAddress = name && address && !address.startsWith(name)
    ? `${name}, ${address}`
    : address || name;
  const destinationLocation = details.geometry?.location || place.geometry?.location;

  return {
    pickupAddress: pickup.address,
    pickupLatitude: pickup.location.latitude,
    pickupLongitude: pickup.location.longitude,
    destinationAddress,
    destinationLatitude: destinationLocation?.lat?.() || '',
    destinationLongitude: destinationLocation?.lng?.() || ''
  };
};

const parseDateInput = (value) => {
  if (!value) return null;
  const date = new Date(`${value}T12:00:00`);
  return Number.isNaN(date.getTime()) ? null : date;
};

const calculateSuggestedMaxRides = ({ validFrom, validUntil, ridesPerWeek, outboundTrip, returnTrip }) => {
  const start = parseDateInput(validFrom);
  const end = parseDateInput(validUntil);
  const weeklyRides = Number(ridesPerWeek);

  if (!start || !end || end < start || !Number.isFinite(weeklyRides) || weeklyRides < 1) {
    return null;
  }

  const daysInclusive = Math.floor((end.getTime() - start.getTime()) / 86400000) + 1;
  const directionFactor = outboundTrip && returnTrip ? 2 : 1;

  return Math.ceil((daysInclusive / 7) * weeklyRides) * directionFactor;
};

const emptyEligibilitySnapshot = {
  mobilityMarks: [],
  careLevel: '',
  permanentMobilityImpairment: false,
  proofType: '',
  notes: ''
};

const normalizeEligibilitySnapshot = (value = {}) => ({
  mobilityMarks: Array.isArray(value.mobilityMarks) ? value.mobilityMarks.filter(Boolean) : [],
  careLevel: value.careLevel ? String(value.careLevel) : '',
  permanentMobilityImpairment: value.permanentMobilityImpairment === true,
  proofType: value.proofType || '',
  notes: value.notes || ''
});

const buildCustomerEligibilitySnapshot = (customer) => {
  const eligibility = customer?.medicalTransportEligibility || customer?.user?.medicalTransportEligibility || {};
  return normalizeEligibilitySnapshot({
    mobilityMarks: eligibility.mobilityMarks,
    careLevel: eligibility.careLevel,
    permanentMobilityImpairment: eligibility.permanentMobilityImpairment,
    proofType: eligibility.mobilityMarks?.length || eligibility.careLevel ? 'customer_master' : '',
    notes: eligibility.notes || ''
  });
};

const assessEligibilitySnapshot = (eligibility, source = 'manual') => {
  const marks = Array.isArray(eligibility.mobilityMarks) ? eligibility.mobilityMarks : [];
  const careLevel = eligibility.careLevel ? String(eligibility.careLevel) : '';
  const hasPermanentImpairment = eligibility.permanentMobilityImpairment === true;
  const sourceText = source === 'customer_master' ? 'Im Kundenstamm' : 'In dieser Verordnung';

  if (marks.some(mark => ['aG', 'Bl', 'H'].includes(mark))) {
    return {
      personGroupStatus: 'eligible',
      personGroupSource: source,
      personGroupHint: `${sourceText} ist Merkzeichen ${marks.join(', ')} hinterlegt. Nachweis vor Abrechnung prüfen.`
    };
  }

  if (['4', '5'].includes(careLevel)) {
    return {
      personGroupStatus: 'eligible',
      personGroupSource: source,
      personGroupHint: `${sourceText} ist Pflegegrad ${careLevel} hinterlegt. Nachweis vor Abrechnung prüfen.`
    };
  }

  if (careLevel === '3') {
    return {
      personGroupStatus: hasPermanentImpairment ? 'eligible' : 'needs_review',
      personGroupSource: source,
      personGroupHint: hasPermanentImpairment
        ? `${sourceText} ist Pflegegrad 3 mit dauerhafter Mobilitätsbeeinträchtigung hinterlegt. Nachweis vor Abrechnung prüfen.`
        : `${sourceText} ist Pflegegrad 3 hinterlegt, die dauerhafte Mobilitätsbeeinträchtigung ist aber nicht bestätigt. Manuell prüfen.`
    };
  }

  return {
    personGroupStatus: 'unknown',
    personGroupSource: 'manual',
    personGroupHint: ''
  };
};

const emptyForm = {
  customerId: '',
  patientName: '',
  patientBirthDate: '',
  patientPhone: '',
  healthInsuranceName: '',
  insuranceNumber: '',
  prescriptionNumber: '',
  approvalNumber: '',
  approvalRequired: true,
  approvalStatus: 'missing',
  approvalHint: '',
  personGroupStatus: 'unknown',
  personGroupHint: '',
  personGroupSource: 'manual',
  medicalTransportEligibilitySnapshot: emptyEligibilitySnapshot,
  approvalRequestedAt: '',
  approvalApprovedAt: '',
  approvalValidUntil: '',
  validFrom: '',
  validUntil: '',
  maxRides: '',
  ridesPerWeek: '',
  usedRides: 0,
  plannedRides: 0,
  routeScope: 'fixed_route',
  outboundTrip: false,
  returnTrip: false,
  pickupAddress: '',
  pickupLatitude: '',
  pickupLongitude: '',
  destinationAddress: '',
  destinationLatitude: '',
  destinationLongitude: '',
  treatmentType: 'other',
  transportRequirementMode: 'vehicle_type',
  requiredCapabilities: [],
  defaultVehicleType: 1,
  defaultNotes: '',
  escortRequired: false,
  copaymentStatus: 'unknown',
  copaymentHint: '',
  copaymentAmountEur: '',
  documentStatus: 'incomplete',
  billingStatus: 'not_ready',
  prescriptionScanRef: null,
  prescriptionScanId: null,
  prescriptionScanConfidence: null,
  operatorId: ''
};

const optionItems = (options) => options.map(option => (
  <MenuItem key={option.value} value={option.value}>
    {option.label}
  </MenuItem>
));

const MedicalTransportOrderForm = () => {
  const { id } = useParams();
  const location = useLocation();
  const isRenewMode = Boolean(id) && location.pathname.endsWith('/erneuern');
  const isEditMode = Boolean(id) && !isRenewMode;
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { currentOrder, isLoading, isSaving, error } = useSelector(state => state.medicalTransportOrders);
  const { customers, isLoading: customersLoading } = useSelector(state => state.customers);
  const [form, setForm] = useState(emptyForm);
  const [formErrors, setFormErrors] = useState({});
  const [scanResult, setScanResult] = useState(null);
  const [scanError, setScanError] = useState('');
  const [isScanning, setIsScanning] = useState(false);
  const [maxRidesManuallyEdited, setMaxRidesManuallyEdited] = useState(false);

  useEffect(() => {
    dispatch(fetchCustomers());
    if (isEditMode || isRenewMode) {
      dispatch(fetchMedicalTransportOrderById(id));
    } else {
      dispatch(clearCurrentMedicalTransportOrder());
      setForm(emptyForm);
      setMaxRidesManuallyEdited(false);
    }
  }, [dispatch, id, isEditMode, isRenewMode]);

  useEffect(() => {
    if ((!isEditMode && !isRenewMode) || !currentOrder) return;
    if (id && currentOrder.id !== id) return;
    setForm({
      customerId: currentOrder.customerId || '',
      patientName: currentOrder.patientName || '',
      patientBirthDate: toDateInputValue(currentOrder.patientBirthDate),
      patientPhone: currentOrder.patientPhone || '',
      healthInsuranceName: currentOrder.healthInsuranceName || '',
      insuranceNumber: currentOrder.insuranceNumber || '',
      prescriptionNumber: isRenewMode ? '' : (currentOrder.prescriptionNumber || ''),
      approvalNumber: isRenewMode ? '' : (currentOrder.approvalNumber || ''),
      approvalRequired: currentOrder.approvalRequired !== false,
      approvalStatus: isRenewMode ? 'missing' : (currentOrder.approvalStatus || 'missing'),
      approvalHint: isRenewMode ? '' : (currentOrder.approvalHint || ''),
      personGroupStatus: currentOrder.personGroupStatus || 'unknown',
      personGroupHint: currentOrder.personGroupHint || '',
      personGroupSource: currentOrder.personGroupSource || 'manual',
      medicalTransportEligibilitySnapshot: normalizeEligibilitySnapshot(currentOrder.medicalTransportEligibilitySnapshot),
      approvalRequestedAt: isRenewMode ? '' : toDateInputValue(currentOrder.approvalRequestedAt),
      approvalApprovedAt: isRenewMode ? '' : toDateInputValue(currentOrder.approvalApprovedAt),
      approvalValidUntil: isRenewMode ? '' : toDateInputValue(currentOrder.approvalValidUntil),
      validFrom: isRenewMode ? '' : toDateInputValue(currentOrder.validFrom),
      validUntil: isRenewMode ? '' : toDateInputValue(currentOrder.validUntil),
      maxRides: isRenewMode ? '' : (currentOrder.maxRides || ''),
      ridesPerWeek: isRenewMode ? '' : (currentOrder.ridesPerWeek || ''),
      usedRides: isRenewMode ? 0 : (currentOrder.usedRides || 0),
      plannedRides: isRenewMode ? 0 : (currentOrder.plannedRides || 0),
      routeScope: currentOrder.routeScope || 'fixed_route',
      outboundTrip: currentOrder.outboundTrip === true,
      returnTrip: currentOrder.returnTrip === true,
      pickupAddress: currentOrder.pickupAddress || '',
      pickupLatitude: currentOrder.pickupLatitude || '',
      pickupLongitude: currentOrder.pickupLongitude || '',
      destinationAddress: currentOrder.destinationAddress || '',
      destinationLatitude: currentOrder.destinationLatitude || '',
      destinationLongitude: currentOrder.destinationLongitude || '',
      treatmentType: currentOrder.treatmentType || 'other',
      transportRequirementMode: currentOrder.transportRequirementMode || (currentOrder.requiredCapabilities?.length ? 'capability' : 'vehicle_type'),
      requiredCapabilities: currentOrder.requiredCapabilities || [],
      defaultVehicleType: currentOrder.defaultVehicleType || 1,
      defaultNotes: currentOrder.defaultNotes || '',
      escortRequired: currentOrder.escortRequired === true,
      copaymentStatus: currentOrder.copaymentStatus || 'unknown',
      copaymentHint: currentOrder.copaymentHint || '',
      copaymentAmountEur: currentOrder.copaymentAmountEur || '',
      documentStatus: isRenewMode ? 'incomplete' : (currentOrder.documentStatus || 'incomplete'),
      billingStatus: isRenewMode ? 'not_ready' : (currentOrder.billingStatus || 'not_ready'),
      prescriptionScanRef: currentOrder.prescriptionScanRef || null,
      prescriptionScanId: currentOrder.prescriptionScanId || null,
      prescriptionScanConfidence: currentOrder.prescriptionScanConfidence || null,
      operatorId: currentOrder.operatorId || ''
    });
    setMaxRidesManuallyEdited(!isRenewMode && Boolean(currentOrder.maxRides));
  }, [currentOrder, id, isEditMode, isRenewMode]);

  const capabilityOptions = useMemo(
    () => Object.entries(SPECIAL_CAPABILITIES).map(([value, label]) => ({ value, label })),
    []
  );

  const selectedCustomer = useMemo(
    () => customers.find(customer => customer.id === form.customerId) || null,
    [customers, form.customerId]
  );

  const suggestedMaxRides = useMemo(
    () => calculateSuggestedMaxRides(form),
    [form]
  );

  useEffect(() => {
    if (!suggestedMaxRides || maxRidesManuallyEdited) return;
    setForm(prev => (
      String(prev.maxRides || '') === String(suggestedMaxRides)
        ? prev
        : { ...prev, maxRides: suggestedMaxRides }
    ));
  }, [suggestedMaxRides, maxRidesManuallyEdited]);

  const maxRidesHelperText = formErrors.maxRides || (
    suggestedMaxRides
      ? maxRidesManuallyEdited && Number(form.maxRides) !== suggestedMaxRides
        ? `Vorschlag: ${suggestedMaxRides} aus Zeitraum, Fahrten/Woche und Richtung.`
        : 'Automatisch aus Zeitraum, Fahrten/Woche und Richtung berechnet.'
      : ''
  );

  const handleChange = (field) => (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    if (field === 'maxRides') {
      setMaxRidesManuallyEdited(value !== '');
    }
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleCustomerChange = (event, customer) => {
    const eligibilitySnapshot = customer ? buildCustomerEligibilitySnapshot(customer) : null;
    const personGroup = eligibilitySnapshot ? assessEligibilitySnapshot(eligibilitySnapshot, 'customer_master') : null;
    setForm(prev => ({
      ...prev,
      customerId: customer?.id || '',
      patientName: customer ? (customer.name || customer.user?.name || prev.patientName) : prev.patientName,
      patientBirthDate: customer ? toDateInputValue(customer.birthDate || customer.user?.birthDate || prev.patientBirthDate) : prev.patientBirthDate,
      patientPhone: customer ? (customer.phoneNumber || customer.phone || customer.user?.phoneNumber || prev.patientPhone) : prev.patientPhone,
      pickupAddress: customer ? (customer.address || customer.user?.address || prev.pickupAddress) : prev.pickupAddress,
      medicalTransportEligibilitySnapshot: eligibilitySnapshot || prev.medicalTransportEligibilitySnapshot,
      ...(personGroup || {})
    }));
  };

  const handleEligibilitySnapshotChange = (field) => (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setForm(prev => {
      const snapshot = normalizeEligibilitySnapshot({
        ...prev.medicalTransportEligibilitySnapshot,
        [field]: value
      });
      return {
        ...prev,
        medicalTransportEligibilitySnapshot: snapshot,
        ...assessEligibilitySnapshot(snapshot, 'manual')
      };
    });
  };

  const handleTransportRequirementModeChange = (event) => {
    const mode = event.target.value;
    setForm(prev => ({
      ...prev,
      transportRequirementMode: mode,
      defaultVehicleType: mode === 'vehicle_type' ? (prev.defaultVehicleType || 1) : '',
      requiredCapabilities: mode === 'capability' ? prev.requiredCapabilities : []
    }));
  };

  const handlePickupSelect = (address, location) => {
    setForm(prev => ({
      ...prev,
      pickupAddress: address,
      pickupLatitude: location?.latitude || '',
      pickupLongitude: location?.longitude || ''
    }));
  };

  const handleDestinationSelect = (address, location) => {
    setForm(prev => ({
      ...prev,
      destinationAddress: address,
      destinationLatitude: location?.latitude || '',
      destinationLongitude: location?.longitude || ''
    }));
  };

  const handlePrescriptionScan = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    setIsScanning(true);
    setScanError('');
    setScanResult(null);

    try {
      const result = await uploadAndScanPrescription(file);
      const patch = mapScanToFormState(result);
      setMaxRidesManuallyEdited(false);
      const basePatch = { ...patch };
      if (isGenericFacilityHint(basePatch.destinationAddress)) {
        delete basePatch.destinationAddress;
      }
      const routePatch = await findNearestFacility({
        pickupAddress: basePatch.pickupAddress || form.pickupAddress,
        treatmentType: basePatch.treatmentType,
        facilityHint: patch.destinationAddress
      }).catch(error => {
        console.warn('Automatische Zielauflösung fehlgeschlagen:', error);
        return null;
      });
      const nextPatch = {
        ...basePatch,
        ...mergeRoutePatchWithoutAddressRegression(basePatch, routePatch)
      };
      setForm(prev => ({
        ...prev,
        ...nextPatch,
        personGroupStatus: prev.personGroupSource === 'customer_master' && prev.personGroupStatus === 'eligible'
          ? prev.personGroupStatus
          : (nextPatch.personGroupStatus || prev.personGroupStatus),
        personGroupHint: prev.personGroupSource === 'customer_master' && prev.personGroupStatus === 'eligible'
          ? prev.personGroupHint
          : (nextPatch.personGroupHint || prev.personGroupHint),
        personGroupSource: prev.personGroupSource === 'customer_master' && prev.personGroupStatus === 'eligible'
          ? prev.personGroupSource
          : (nextPatch.personGroupSource || prev.personGroupSource),
        defaultNotes: nextPatch.defaultNotes
          ? `${prev.defaultNotes ? `${prev.defaultNotes}\n\n` : ''}OCR-Auszug:\n${nextPatch.defaultNotes}`
          : prev.defaultNotes
      }));
      setScanResult(result);
      setFormErrors(prev => {
        const next = { ...prev };
        Object.keys(nextPatch).forEach(key => delete next[key]);
        return next;
      });
    } catch (scanException) {
      setScanError(scanException.message || 'Verordnung konnte nicht gescannt werden.');
    } finally {
      setIsScanning(false);
    }
  };

  const validate = () => {
    const errors = {};
    if (!form.patientName.trim()) errors.patientName = 'Patient ist erforderlich';
    if (!form.patientBirthDate) {
      errors.patientBirthDate = 'Geburtsdatum ist erforderlich';
    } else {
      const birthDate = new Date(`${form.patientBirthDate}T00:00:00`);
      const minBirthDate = new Date('1900-01-01T00:00:00');
      const today = new Date();
      today.setHours(23, 59, 59, 999);
      if (Number.isNaN(birthDate.getTime()) || birthDate < minBirthDate || birthDate > today) {
        errors.patientBirthDate = 'Bitte ein plausibles Geburtsdatum eingeben';
      }
    }
    if (!form.healthInsuranceName.trim()) errors.healthInsuranceName = 'Krankenkasse ist erforderlich';
    if (!form.validFrom) errors.validFrom = 'Gültig ab ist erforderlich';
    if (!form.validUntil) errors.validUntil = 'Gültig bis ist erforderlich';
    if (form.validFrom && form.validUntil && new Date(form.validFrom) > new Date(form.validUntil)) {
      errors.validUntil = 'Gültig bis muss nach Gültig ab liegen';
    }
    if (!form.pickupAddress.trim()) errors.pickupAddress = 'Startadresse ist erforderlich';
    if (!form.destinationAddress.trim()) errors.destinationAddress = 'Ziel/Einrichtung ist erforderlich';
    if (!Number(form.maxRides) || Number(form.maxRides) < 1) {
      errors.maxRides = 'Mindestens eine Fahrt ist erforderlich';
    }
    if (form.ridesPerWeek && Number(form.ridesPerWeek) < 1) {
      errors.ridesPerWeek = 'Bitte eine positive Anzahl eintragen';
    }
    if (form.transportRequirementMode === 'vehicle_type' && !form.defaultVehicleType) {
      errors.defaultVehicleType = 'Bitte Fahrzeugtyp auswählen';
    }
    if (form.transportRequirementMode === 'capability' && form.requiredCapabilities.length === 0) {
      errors.requiredCapabilities = 'Bitte mindestens eine Spezialfähigkeit auswählen';
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validate()) return;

    const action = isEditMode
      ? updateMedicalTransportOrder({ id, ...form })
      : createMedicalTransportOrder({
          ...form,
          ...(isRenewMode ? { renewedFromOrderId: id } : {})
        });
    const result = await dispatch(action);
    if (!result.error) {
      navigate(`/krankenfahrten/${result.payload.id}`);
    }
  };

  if ((isEditMode || isRenewMode) && isLoading && !currentOrder) {
    return <Loading message="Verordnung wird geladen..." />;
  }

  if ((isEditMode || isRenewMode) && error && !currentOrder) {
    return (
      <ErrorMessage
        title="Fehler beim Laden der Verordnung"
        message={error}
        onRetry={() => dispatch(fetchMedicalTransportOrderById(id))}
      />
    );
  }

  return (
    <APIProvider apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY}>
    <Box sx={{ flexGrow: 1, p: 3 }}>
      <PageHeader
        title={isRenewMode ? 'Verordnung erneuern' : isEditMode ? 'Verordnung bearbeiten' : 'Verordnung anlegen'}
        description={isRenewMode ? 'Neue Verordnung mit Datenübernahme aus bestehendem Fall erstellen' : 'Verordnungsfall für wiederkehrende Krankenfahrten erfassen'}
        breadcrumbs={[
          { label: 'Dashboard', path: '/dashboard' },
          { label: 'Krankenfahrten', path: '/krankenfahrten' },
          { label: isRenewMode ? 'Erneuern' : isEditMode ? 'Bearbeiten' : 'Neu', path: isRenewMode ? `/krankenfahrten/${id}/erneuern` : isEditMode ? `/krankenfahrten/${id}/bearbeiten` : '/krankenfahrten/neu' }
        ]}
      />

      {isRenewMode && currentOrder && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Stammdaten, Strecke, Sonderbedarf und Hinweise wurden übernommen. Verordnungs-/Genehmigungsnummer,
          Genehmigungsdaten, Zeitraum, Kontingent, Dokument- und Abrechnungsstatus werden für die neue Verordnung neu erfasst.
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {scanError && (
        <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setScanError('')}>
          {scanError}
        </Alert>
      )}

      {scanResult && (
        <Alert severity={scanResult.status === 'completed' ? 'success' : 'info'} sx={{ mb: 2 }}>
          Scan übernommen. Bitte erkannte Felder prüfen
          {scanResult.warnings?.length ? `: ${scanResult.warnings.join(' ')}` : '.'}
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }} justifyContent="space-between">
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              Verordnung scannen
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Foto oder PDF hochladen, Felder automatisch auslesen und anschließend manuell prüfen.
            </Typography>
          </Box>
          <Button
            component="label"
            variant="contained"
            startIcon={<UploadFileIcon />}
            disabled={isScanning || isSaving}
          >
            {isScanning ? 'Scan läuft...' : 'Scan hochladen'}
            <input
              hidden
              type="file"
              accept="image/*,application/pdf"
              onChange={handlePrescriptionScan}
            />
          </Button>
        </Stack>
      </Paper>

      <Box component="form" onSubmit={handleSubmit}>
        <Box sx={{ columnCount: { xs: 1, lg: 2 }, columnGap: 3 }}>
          <Box sx={{ breakInside: 'avoid', mb: 3 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Patient und Krankenkasse</Typography>
                <Grid container spacing={2}>
                  <Grid size={12}>
                    <Autocomplete
                      options={customers}
                      loading={customersLoading}
                      value={selectedCustomer}
                      onChange={handleCustomerChange}
                      getOptionLabel={(option) => option?.name || option?.user?.name || option?.email || ''}
                      isOptionEqualToValue={(option, value) => option.id === value.id}
                      filterOptions={(options, params) => {
                        const term = params.inputValue.toLowerCase();
                        if (!term) return options;
                        return options.filter((option) => {
                          const name = (option.name || option.user?.name || '').toLowerCase();
                          const phone = (option.phoneNumber || option.phone || option.user?.phoneNumber || '').toLowerCase();
                          const email = (option.email || option.user?.email || '').toLowerCase();
                          return name.includes(term) || phone.includes(term) || email.includes(term);
                        });
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="App-Kunde verknüpfen"
                          helperText="Optional: Verknüpft die Verordnung mit einem registrierten Kundenkonto für spätere App-Bestellungen."
                          InputProps={{
                            ...params.InputProps,
                            startAdornment: (
                              <>
                                <PersonIcon color="primary" sx={{ mr: 1 }} />
                                {params.InputProps.startAdornment}
                              </>
                            )
                          }}
                        />
                      )}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField label="Patient" value={form.patientName} onChange={handleChange('patientName')} error={!!formErrors.patientName} helperText={formErrors.patientName} fullWidth required />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField label="Geburtsdatum" type="date" value={form.patientBirthDate} onChange={handleChange('patientBirthDate')} error={!!formErrors.patientBirthDate} helperText={formErrors.patientBirthDate} slotProps={{ inputLabel: { shrink: true } }} fullWidth required />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField label="Telefon" value={form.patientPhone} onChange={handleChange('patientPhone')} fullWidth />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField label="Krankenkasse" value={form.healthInsuranceName} onChange={handleChange('healthInsuranceName')} error={!!formErrors.healthInsuranceName} helperText={formErrors.healthInsuranceName} fullWidth required />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField label="Versichertennummer" value={form.insuranceNumber} onChange={handleChange('insuranceNumber')} fullWidth />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Box>

          <Box sx={{ breakInside: 'avoid', mb: 3 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Genehmigung</Typography>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField label="Verordnungsnummer" value={form.prescriptionNumber} onChange={handleChange('prescriptionNumber')} fullWidth />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField label="Genehmigungsnummer" value={form.approvalNumber} onChange={handleChange('approvalNumber')} fullWidth />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField select label="Genehmigungsstatus" value={form.approvalStatus} onChange={handleChange('approvalStatus')} fullWidth>
                      {optionItems(APPROVAL_STATUS_OPTIONS)}
                    </TextField>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField select label="Personenkreis" value={form.personGroupStatus} onChange={handleChange('personGroupStatus')} fullWidth>
                      {optionItems(PERSON_GROUP_STATUS_OPTIONS)}
                    </TextField>
                  </Grid>
                  <Grid size={12}>
                    <Typography variant="subtitle2" sx={{ mt: 1 }}>
                      Personenkreis / Nachweise
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Snapshot für diese Verordnung. Wird aus dem Kundenstamm übernommen, kann aber für diesen Fall korrigiert werden.
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <FormControl fullWidth>
                      <InputLabel id="order-mobility-marks-label">Merkzeichen</InputLabel>
                      <Select
                        labelId="order-mobility-marks-label"
                        multiple
                        value={form.medicalTransportEligibilitySnapshot.mobilityMarks}
                        onChange={handleEligibilitySnapshotChange('mobilityMarks')}
                        input={<OutlinedInput label="Merkzeichen" />}
                        renderValue={(selected) => selected.join(', ')}
                      >
                        {MOBILITY_MARK_OPTIONS.map(option => (
                          <MenuItem key={option.value} value={option.value}>
                            <Checkbox checked={form.medicalTransportEligibilitySnapshot.mobilityMarks.includes(option.value)} />
                            <ListItemText primary={option.label} />
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      select
                      label="Pflegegrad"
                      value={form.medicalTransportEligibilitySnapshot.careLevel}
                      onChange={handleEligibilitySnapshotChange('careLevel')}
                      fullWidth
                    >
                      {optionItems(CARE_LEVEL_OPTIONS)}
                    </TextField>
                  </Grid>
                  <Grid size={12}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={form.medicalTransportEligibilitySnapshot.permanentMobilityImpairment}
                          onChange={handleEligibilitySnapshotChange('permanentMobilityImpairment')}
                        />
                      }
                      label="Bei Pflegegrad 3 liegt eine dauerhafte Mobilitätsbeeinträchtigung vor"
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      select
                      label="Nachweisart"
                      value={form.medicalTransportEligibilitySnapshot.proofType}
                      onChange={handleEligibilitySnapshotChange('proofType')}
                      fullWidth
                    >
                      {optionItems(ELIGIBILITY_PROOF_TYPE_OPTIONS)}
                    </TextField>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField label="Genehmigt bis" type="date" value={form.approvalValidUntil} onChange={handleChange('approvalValidUntil')} slotProps={{ inputLabel: { shrink: true } }} fullWidth />
                  </Grid>
                  <Grid size={12}>
                    <FormControlLabel
                      control={<Checkbox checked={form.approvalRequired} onChange={handleChange('approvalRequired')} />}
                      label="Genehmigung erforderlich"
                    />
                  </Grid>
                  {form.approvalHint && (
                    <Grid size={12}>
                      <Alert severity={form.approvalRequired ? 'warning' : 'info'}>
                        {form.approvalHint}
                      </Alert>
                    </Grid>
                  )}
                  {form.personGroupHint && (
                    <Grid size={12}>
                      <Alert severity={form.personGroupStatus === 'eligible' ? 'success' : 'warning'}>
                        {form.personGroupHint}
                      </Alert>
                    </Grid>
                  )}
                  <Grid size={12}>
                    <TextField
                      label="Interne Notiz zum Nachweis"
                      value={form.medicalTransportEligibilitySnapshot.notes}
                      onChange={handleEligibilitySnapshotChange('notes')}
                      helperText="Zum Beispiel: SBA liegt vor, Pflegegradbescheid geprüft, Nachweis noch offen."
                      fullWidth
                      multiline
                      rows={2}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Box>

          <Box sx={{ breakInside: 'avoid', mb: 3 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Zeitraum und Kontingent</Typography>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 3 }}>
                    <TextField label="Gültig ab" type="date" value={form.validFrom} onChange={handleChange('validFrom')} error={!!formErrors.validFrom} helperText={formErrors.validFrom} slotProps={{ inputLabel: { shrink: true } }} fullWidth required />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 3 }}>
                    <TextField label="Gültig bis" type="date" value={form.validUntil} onChange={handleChange('validUntil')} error={!!formErrors.validUntil} helperText={formErrors.validUntil} slotProps={{ inputLabel: { shrink: true } }} fullWidth required />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 3 }}>
                    <TextField label="Max. Fahrten" type="number" value={form.maxRides} onChange={handleChange('maxRides')} error={!!formErrors.maxRides} helperText={maxRidesHelperText} fullWidth required />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 3 }}>
                    <TextField label="Fahrten/Woche" type="number" value={form.ridesPerWeek} onChange={handleChange('ridesPerWeek')} error={!!formErrors.ridesPerWeek} helperText={formErrors.ridesPerWeek} fullWidth />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField label="Geplante Fahrten" type="number" value={form.plannedRides} onChange={handleChange('plannedRides')} fullWidth />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField label="Genutzte Fahrten" type="number" value={form.usedRides} onChange={handleChange('usedRides')} fullWidth />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Box>

          <Box sx={{ breakInside: 'avoid', mb: 3 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Strecke und Behandlung</Typography>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField select label="Streckenbindung" value={form.routeScope} onChange={handleChange('routeScope')} fullWidth>
                      {optionItems(ROUTE_SCOPE_OPTIONS)}
                    </TextField>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField select label="Behandlungstyp" value={form.treatmentType} onChange={handleChange('treatmentType')} fullWidth>
                      {optionItems(TREATMENT_TYPE_OPTIONS)}
                    </TextField>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <FormControlLabel
                      control={<Checkbox checked={form.outboundTrip} onChange={handleChange('outboundTrip')} />}
                      label="Hinfahrt verordnet"
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <FormControlLabel
                      control={<Checkbox checked={form.returnTrip} onChange={handleChange('returnTrip')} />}
                      label="Rückfahrt verordnet"
                    />
                  </Grid>
                  <Grid size={12}>
                    <PlaceAutocomplete
                      label="Startadresse"
                      value={form.pickupAddress}
                      onChange={(event) => setForm(prev => ({ ...prev, pickupAddress: event.target.value }))}
                      onPlaceSelect={handlePickupSelect}
                      error={!!formErrors.pickupAddress}
                      helperText={formErrors.pickupAddress}
                      fullWidth
                      required
                    />
                  </Grid>
                  <Grid size={12}>
                    <PlaceAutocomplete
                      label="Ziel / Einrichtung"
                      value={form.destinationAddress}
                      onChange={(event) => setForm(prev => ({ ...prev, destinationAddress: event.target.value }))}
                      onPlaceSelect={handleDestinationSelect}
                      error={!!formErrors.destinationAddress}
                      helperText={formErrors.destinationAddress}
                      fullWidth
                      required
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Box>

          <Box sx={{ breakInside: 'avoid', mb: 3 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Sonderbedarf</Typography>
                <Grid container spacing={2}>
                  <Grid size={12}>
                    <TextField select label="Bedarfsart" value={form.transportRequirementMode} onChange={handleTransportRequirementModeChange} fullWidth>
                      <MenuItem value="vehicle_type">Fahrzeugtyp festlegen</MenuItem>
                      <MenuItem value="capability">Spezialfähigkeit festlegen</MenuItem>
                    </TextField>
                  </Grid>
                  {form.transportRequirementMode === 'vehicle_type' && (
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField select label="Standard-Fahrzeugtyp" value={form.defaultVehicleType} onChange={handleChange('defaultVehicleType')} error={!!formErrors.defaultVehicleType} helperText={formErrors.defaultVehicleType} fullWidth>
                        {VEHICLE_TYPE_OPTIONS.map(option => (
                          <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                  )}
                  {form.transportRequirementMode === 'capability' && (
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <FormControl fullWidth>
                        <InputLabel id="required-capabilities-label">Spezialfähigkeiten</InputLabel>
                        <Select
                          labelId="required-capabilities-label"
                          multiple
                          value={form.requiredCapabilities}
                          onChange={handleChange('requiredCapabilities')}
                          input={<OutlinedInput label="Spezialfähigkeiten" />}
                          renderValue={(selected) => selected.map(value => SPECIAL_CAPABILITIES[value] || value).join(', ')}
                          error={!!formErrors.requiredCapabilities}
                        >
                          {capabilityOptions.map(option => (
                            <MenuItem key={option.value} value={option.value}>
                              <Checkbox checked={form.requiredCapabilities.includes(option.value)} />
                              <ListItemText primary={option.label} />
                            </MenuItem>
                          ))}
                        </Select>
                        {formErrors.requiredCapabilities && (
                          <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                            {formErrors.requiredCapabilities}
                          </Typography>
                        )}
                      </FormControl>
                    </Grid>
                  )}
                  <Grid size={12}>
                    <FormControlLabel
                      control={<Checkbox checked={form.escortRequired} onChange={handleChange('escortRequired')} />}
                      label="Begleitperson erforderlich"
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Box>

          <Box sx={{ breakInside: 'avoid', mb: 3 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Nachweis und Abrechnung</Typography>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <TextField select label="Zuzahlung" value={form.copaymentStatus} onChange={handleChange('copaymentStatus')} fullWidth>
                      {optionItems(COPAYMENT_STATUS_OPTIONS)}
                    </TextField>
                  </Grid>
                  {form.copaymentHint && (
                    <Grid size={12}>
                      <Alert severity={form.copaymentStatus === 'required' ? 'warning' : 'info'}>
                        {form.copaymentHint}
                      </Alert>
                    </Grid>
                  )}
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <TextField select label="Dokumente" value={form.documentStatus} onChange={handleChange('documentStatus')} fullWidth>
                      {optionItems(DOCUMENT_STATUS_OPTIONS)}
                    </TextField>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <TextField select label="Abrechnung" value={form.billingStatus} onChange={handleChange('billingStatus')} fullWidth>
                      {optionItems(BILLING_STATUS_OPTIONS)}
                    </TextField>
                  </Grid>
                  <Grid size={12}>
                    <TextField
                      label="Interne Notizen / Fahrerhinweise"
                      value={form.defaultNotes}
                      onChange={handleChange('defaultNotes')}
                      fullWidth
                      multiline
                      minRows={4}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Box>
        </Box>

        <Paper sx={{ mt: 3, p: 2 }}>
          <Stack direction="row" spacing={2} justifyContent="flex-end">
            <Button variant="outlined" onClick={() => navigate('/krankenfahrten')}>
              Abbrechen
            </Button>
            <Button type="submit" variant="contained" startIcon={<SaveIcon />} disabled={isSaving}>
              {isSaving ? 'Speichert...' : 'Speichern'}
            </Button>
          </Stack>
        </Paper>
      </Box>
    </Box>
    </APIProvider>
  );
};

export default MedicalTransportOrderForm;
